<template lang="pug">
.wrapper
	span.no-results-title {{ t('search.term.notFound.queueTitle') }}
	.no-results-body
		span {{ t(isSongsPage ? 'search.term.notFound.filtersSubtitle' : 'search.term.notFound.queueSubtitle') }}
		.selected-filters(v-if="isSongsPage && selectedFilters.length > 0")
			.buttons-wrapper
				SingaButton.filter-button.is-transparent-dark.is-rounded(v-for="filter in selectedFilters" :key="filter.name" :icon-right="'close-circle-outline'" size="small" @click="removeFilter(filter.name)") {{ filter.translationKey }}
				SingaButton.filter-button.is-transparent-dark.is-rounded(v-if="selectedFilters && selectedFilters.length > 0" :icon-right="'close-circle-outline'" @click="clearFilters") {{ t('filter.clearAll') }}
		.cta
			span(v-if="!isSongsPage") {{ t('search.term.notFound.request') }}
			SongRequestButton.no-results-request(:type="'queue'")
</template>

<script setup lang="ts">
import { useSearchStore } from '@/pinia/searchStore'
import { useGenreStore } from '@/pinia/genreStore'

const { t } = useI18n()

const searchStore = useSearchStore()
const genreStore = useGenreStore()
const allGenres = computed(() => genreStore.allGenres)

defineProps({
	isSongsPage: {
		type: Boolean,
		default: false
	}
})

const selectedFilters = computed(() => {
	const filters: any[] = []
	const query = useRoute().query

	// Extract filters from query params
	const genre = typeof query.genre === 'string' ? query.genre.split(',') : []
	const language = typeof query.language === 'string' ? query.language.split(',') : []
	const decade = typeof query.decade === 'string' ? query.decade.split(',') : []
	const has_cover = query.has_cover === 'true'
	const has_plus = query.has_plus === 'true'
	const has_duet = query.has_duet === 'true'
	const has_original = query.has_original === 'true'

	// Add genres
	if (genre.length > 0) {
		for (const g of genre) {
			const genreName = allGenres.value.find(genre => genre.id.toString() === g)?.name || g
			const translationKey = t(`genre.${genreName}`)
			filters.push({ name: g, translationKey: translationKey.includes('genre.') ? genreName : translationKey, category: 'genre' })
		}
	}

	// Add languages
	if (language.length > 0) {
		for (const l of language) {
			const translationKey = t(`languages.${l}`)
			filters.push({ name: l, translationKey: translationKey.includes('languages.') ? l : translationKey, category: 'language' })
		}
	}

	// Add decades
	if (decade.length > 0) {
		for (const d of decade) {
			const translation = t(`decade.${d}s`)
			filters.push({ name: d, translationKey: translation, category: 'decade' })
		}
	}

	// Add other boolean filters
	const tagMappings = {
		has_cover: 'vocals',
		has_plus: 'plus',
		has_duet: 'duet',
		has_original: 'original'
	}
	for (const [key, value] of Object.entries({ has_cover, has_plus, has_duet, has_original })) {
		if (value) {
			const tagKey = tagMappings[key as keyof typeof tagMappings]
			const translationKey = t(`tags.${tagKey}`)
			filters.push({ name: key, translationKey, category: 'tag' })
		}
	}

	return filters
})

// Only update the results for filters that are not empty
const updateResults = async () => {
	const { genre, language, has_cover, has_plus, has_duet, has_original, decade } = searchStore.getFilters
	const payload: Record<string, any> = {}

	if (genre.length > 0) payload.genre = genre.join(',')
	else payload.genre = undefined
	if (language.length > 0) payload.language = language.join(',')
	else payload.language = undefined
	if (has_cover) payload.has_cover = true
	if (has_plus) payload.has_plus = true
	if (has_duet) payload.has_duet = true
	if (has_original) payload.has_original = true

	// normalize decade field to array then join into comma-separated string
	const decadesArr = Array.isArray(decade) ? decade : (typeof decade === 'string' ? decade.split(',') : [])
	if (decadesArr.length) payload.decade = decadesArr.join(',')
	else payload.decade = undefined

	useEventEmit('searchFilters:updateResults', payload)
}

const removeFilter = (filterName: string) => {
	// remove the selected filter and rebuild all categories
	const remaining = selectedFilters.value.filter(f => f.name !== filterName)
	const updatedGenres = remaining.filter(f => f.category === 'genre').map(f => f.name)
	searchStore.addToSelectedFilter('genre', updatedGenres)
	const updatedLanguages = remaining.filter(f => f.category === 'language').map(f => f.name)
	searchStore.addToSelectedFilter('language', updatedLanguages)
	const updatedDecades = remaining.filter(f => f.category === 'decade').map(f => f.name)
	searchStore.addToSelectedFilter('decade', updatedDecades)
	// update feature filters
	const booleanKeys = ['has_cover', 'has_plus', 'has_duet', 'has_original']
	for (const key of booleanKeys) {
		const isSelected = remaining.some(f => f.name === key)
		searchStore.addToSelectedFilter(key, isSelected)
	}
	updateResults()
}

const clearFilters = () => {
	searchStore.resetSearch()
	// Empty payload to use the default values which set all to undefined
	const payload = {}
	useEventEmit('searchFilters:updateResults', payload)
}
</script>

<style lang="sass" scoped>
.wrapper
	display: flex
	flex-direction: column
	align-items: center
	justify-content: center
	.no-results-title
		@include font(basier, semiBold)
		@include fontSize(m)
		color: $color-grey-30
		margin-bottom: $spacing-8
	.no-results-body
		display: flex
		flex-direction: column
		align-items: center
		@include font(basier, regular)
		@include fontSize(s)
		color: $color-grey-60
		text-align: center
		.cta
			display: flex
			flex-direction: column
			align-items: center
			justify-content: center
			gap: $spacing-8

.no-results-request
	:deep(p)
		color: $color-green-70
		text-decoration: underline
		text-decoration-color: $color-green-70
		text-align: center
		&:hover
			color: $color-green-80
			text-decoration-color: $color-green-80
.selected-filters
	margin: $spacing-32
	display: flex
	flex-wrap: wrap
	justify-content: center
	align-items: center

	.buttons-wrapper
		display: flex
		flex-wrap: wrap
		max-width: 100%
		justify-content: center
		gap: $spacing-16
</style>
